import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatDayInvitationState } from '@chat-emp/shared';

import { BFF_CTA_ROUTES, canClickCta, ctaModeOf, ctaOf } from '../data/cta.js';
import type { CtaButton, CtaPayload, Message, MessageId } from '../fixtures/domain.js';
import { cidParts, emitToHost } from '../lib/bridge.js';
import { refreshLists } from '../inbox/useLists.js';
import { ApiRequestError } from '../lib/api.js';
import { onCtaError } from '../lib/cta.js';
import { postCtaAction, reportCtaClick } from '../lib/facade.js';
import { chatDayInviteCodeOf, useChatDayInvitation } from './useChatDayInvitation.js';

export interface UseMessageCta {
  /** The row, or null when this message has none. */
  cta: CtaPayload | null;
  /** Which button is mid-flight, if any. */
  pendingActionId: string | null;
  /** The whole row is untouchable: spent, barred, or ours to begin with. */
  disabled: boolean;
  /** Why the last tap was refused, in the BFF's words. */
  notice: string | null;
  onButtonClick: (button: CtaButton) => void;
}

/** What a Chat Day card chat-kit has shut says, when nothing sealed it. */
const CLOSED_NOTES: Partial<Record<ChatDayInvitationState, string>> = {
  closed: 'Invitations for this Chat Day have closed.',
  ended: 'This Chat Day has ended.',
};

/**
 * A Chat Day card chat-kit will no longer take an answer on, though its
 * message was never sealed: a tap refused once the link was generated only
 * greys the row for that session. An answer given marks its button, as a seal
 * would; a closed or ended day says so instead.
 */
function chatDayClosure(cta: CtaPayload, state: ChatDayInvitationState | null) {
  if (cta.completed || !state || state === 'open') return null;
  const answered = cta.buttons.find((button) => button.payload?.response === state);
  return {
    cta: answered ? { ...cta, completedActionId: answered.id } : cta,
    note: CLOSED_NOTES[state] ?? null,
  };
}

/**
 * What happens when a CTA button is pressed.
 *
 * The three modes differ in who ends the spinner, and that is the whole design:
 *
 * - `url`    the client POSTs and a backend seals the row. The spinner does NOT
 *            clear on a 2xx — only `message.updated` ends it, because a 2xx says
 *            the request was accepted, not that the action happened.
 * - `event`  the client tells the host and waits. Same rule: the seal or an
 *            error finds us.
 * - `reply`  nobody seals anything. Sending a reply writes a NEW message; it
 *            does not touch the one holding the buttons, and this client cannot
 *            edit someone else's message. So the row greys here, for this
 *            person, for this session, and comes back on reload.
 */
export function useMessageCta(
  message: Message,
  currentUserId: string,
  sendReply: (text: string, quotedMessageId: MessageId, plain?: boolean) => Promise<void>,
): UseMessageCta {
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [locallyCompleted, setLocallyCompleted] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The host reporting that an `event`-mode action failed on its side. Matched
  // by message id: one host message concerns one row.
  useEffect(
    () =>
      onCtaError((detail) => {
        if (detail.messageId === message.id) setPendingActionId(null);
      }),
    [message.id],
  );

  const cta = ctaOf(message);
  const invitation = useChatDayInvitation(chatDayInviteCodeOf(cta));
  const closure = cta ? chatDayClosure(cta, invitation) : null;
  const disabled = !canClickCta(message, currentUserId) || locallyCompleted || closure !== null;

  /**
   * A sealed row has its answer, so there is nothing left for the clicker to
   * wait on: the spinner gives way to the check every other viewer already sees.
   *
   * Derived rather than cleared by an effect. The seal arrives as a re-render
   * with `completed: true`, and the answer has to land on that same frame — an
   * effect would leave the person who clicked staring at a spinner for one more
   * render than everybody else, which is the wrong way round.
   *
   * It reads as spent even when the seal names a different button: someone
   * answered first, and that is the truth about the row.
   */
  const pending = cta?.completed ? null : pendingActionId;

  const onButtonClick = useCallback(
    (button: CtaButton) => {
      if (disabled || pendingActionId !== null) return;
      setPendingActionId(button.id);
      setNotice(null);

      const { channelId } = cidParts(message.channelId);
      const bff =
        button.url !== undefined && BFF_CTA_ROUTES.has(button.url) && ctaModeOf(button) !== 'event';

      // Every click, every mode: the trigger for `event` and the analytics
      // signal for the other two.
      emitToHost('message-cta-click', {
        messageId: message.id,
        channelId,
        actionId: button.id,
        ...(button.payload ? { payload: button.payload } : {}),
      });

      // Records the funnel event and seals the row, so it greys durably. Not for
      // a BFF route: that one seals only once the action was kept.
      if (!bff) {
        void reportCtaClick({
          channelId: message.channelId,
          messageId: message.id,
          ctaId: button.id,
        });
      }

      const announceFailure = (reason: string) => {
        if (!mounted.current) return;
        emitToHost('message-cta-error', { messageId: message.id, actionId: button.id, reason });
      };

      const fail = (reason: string) => {
        if (!mounted.current) return;
        setPendingActionId(null);
        announceFailure(reason);
      };

      const envelope = {
        messageId: message.id,
        channelId,
        actionId: button.id,
        userId: currentUserId,
        payload: button.payload,
      };

      const callUrl = (url: string, onFailure: (reason: string) => void) =>
        fetch(url, {
          method: button.method ?? 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(envelope),
        })
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            // Accepted: keep spinning, the seal ends this rather than a row the backend may refuse.
          })
          .catch((error: unknown) => onFailure(errorText(error)));

      // Reply mode quotes the button message; url_reply posts a plain message.
      const sendReplyText = (text: string, onFailure: (reason: string) => void, plain = false) =>
        sendReply(text, message.id, plain)
          .then(() => {
            if (!mounted.current) return;
            setLocallyCompleted(true);
            setPendingActionId(null);
          })
          .catch((error: unknown) => onFailure(errorText(error)));

      const replyText = button.reply?.trim();

      // A BFF route goes with the session, and the reply waits for it, so a
      // refused answer never posts "I'll join".
      if (bff && button.url) {
        const reply = ctaModeOf(button) === 'url_reply' ? replyText : undefined;
        void postCtaAction(button.url, envelope)
          .then((result) => {
            if ((result as { listsChanged?: boolean } | null)?.listsChanged) void refreshLists();
            if (reply) void sendReplyText(reply, announceFailure, true);
          })
          .catch((error: unknown) => {
            if (!mounted.current) return;
            const api = error instanceof ApiRequestError ? error : null;
            setNotice(api?.message ?? 'That did not go through. Try again.');
            if (api && !api.retryable) setLocallyCompleted(true);
            fail(errorText(error));
          });
        return;
      }

      switch (ctaModeOf(button)) {
        case 'url': {
          // No url means it cannot act: bail rather than strand the row in a spinner.
          if (!button.url) {
            setPendingActionId(null);
            return;
          }
          void callUrl(button.url, fail);
          return;
        }

        case 'reply': {
          if (!replyText) {
            setPendingActionId(null);
            return;
          }
          void sendReplyText(replyText, fail);
          return;
        }

        case 'url_reply': {
          const { url } = button;
          if (url && replyText) {
            // Re-tappable only if both halves failed: a sent reply already answered the row, and
            // an accepted url waits for its seal like plain url mode.
            let failedHalves = 0;
            const failHalf = (reason: string) => {
              failedHalves += 1;
              if (failedHalves === 2) fail(reason);
              else announceFailure(reason);
            };
            void callUrl(url, failHalf);
            void sendReplyText(replyText, failHalf, true);
            return;
          }
          // A missing half degrades to the other mode rather than a tap that silently does nothing.
          if (url) void callUrl(url, fail);
          else if (replyText) void sendReplyText(replyText, fail, true);
          else setPendingActionId(null);
          return;
        }

        case 'event':
          // The click has been announced. Whether anything answers is the host's
          // business; we wait, and the seal or the error will find us.
          return;
      }
    },
    [disabled, pendingActionId, message.id, message.channelId, currentUserId, sendReply],
  );

  return {
    cta: closure?.cta ?? cta,
    pendingActionId: pending,
    disabled,
    notice: notice ?? closure?.note ?? null,
    onButtonClick,
  };
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
